import ModelMappingOrigin from "~/models/mapping/modelMappingOrigin";
import ObjectModel from "~/models/objectModel";
import {IDefaultVOption, IVOptionSetLocal} from "~/interfaces/vSelectInterfaces";
import {ObjectModelPropertyName, ObjectModelPropertyRootType} from "~/enums/objectModelPropertyType";
import ObjectModelProperty from "~/models/objectModelProperties/objectModelProperty";
import { ObjectModelBuiltinCategory, } from "~/enums/objectModelBuiltinPropertyType";
import {accessDisplay, AccessPermission} from "~/enums/accessPermission";
import ObjectModelPropertyDefinition, { BuiltInTypesSetNumber } from "~/models/objectModelProperties/objectModelPropertyDefinition"
import ModelMappingValueOrigin from "~/models/mapping/modelMappingValueOrigin";
import _ from "lodash";
import {ModelMappingAllMatchRules} from '../../../formability/mappingBuilder/enums/model-mapping-rules'

export interface IBuiltInPropertyValue {
  client_id: string,
  defaultLabelTitle: string,
  label_title: string,
  default_property: boolean,
  section: string,
  permissions: AccessPermission[],
  tooltip?: string
}
export default class ModelMappingOriginObjectModel extends ModelMappingOrigin<ObjectModel> {
  /**
   * if mapping straight to object model, it's always single
   * @see {ModelMappingOriginRelationIdentifier} for when mapping is to object model through relation
   */
  isSingle: boolean = true;

  get origin(): ObjectModel {
    return ObjectModel.repo.find(this.id);
  }

  // alias to origin
  get model(): ObjectModel {
    return this.origin
  }

  get identifier(): string {
    return this.origin?.client_id
  }

  get title(): string {
    return this.origin?.titleOrDefault
  }

  get values(): Array<ObjectModelProperty | IBuiltInPropertyValue> {
    return [...this.origin?.objectModelPropertiesArray, ...this.builtInProperties]
  }

  get aboutRecordProperties(): Array<IBuiltInPropertyValue> {
    return this.builtInProperties.filter(e => e.section === ObjectModelBuiltinCategory.AboutRecord)
  }

  get claimingRecordProperties(): Array<IBuiltInPropertyValue> {
    return this.builtInProperties.filter(e => e.section === ObjectModelBuiltinCategory.ClaimingRecord)
  }

  get builtInProperties(): IBuiltInPropertyValue[] {
    const values: IBuiltInPropertyValue[] = []
    for (let detail of ObjectModelPropertyDefinition.builtInTypesSet(this.sourceBuiltInSetNumberForValueOptions)) {
      // get all the enum value keys
      if (detail) {
        const permissions = detail.permissions
        const value = {
          client_id: detail.type.toString(),
          defaultLabelTitle: detail.displayName,
          label_title: detail.displayName,
          permissions: permissions,
          default_property: true,
          section: detail.category,
          labelSuffix: accessDisplay(permissions),
          tooltip: detail.tooltip,
        }
        values.push(value);
      }
    }
    return values;
  }

  vOptionsForBuiltinFields(builtins): Array<IDefaultVOption> {
    return builtins.map(f => {
      return {
        label: f.label_title,
        value: f.client_id,
        labelSuffix: `(${accessDisplay(f.permissions)})`,
      }
    });
  }

  searchValues(query?: string, options: { filters?: ((value: string) => boolean)[], showUnmappedOnly?: boolean } = {}): Partial<this['values']> {
    const filters = [value => value.label_title?.toLowerCase()?.trim()?.includes(query?.toLowerCase()?.trim())]
    if (options.showUnmappedOnly) {
      filters.unshift(value => {
        return !this.modelMapping.isMapped(value.client_id)
      })
    }
    return this.values.filter(value => {
      // If model mapping destination is SObject and Not single, do not allow File Attachment property values
      // !(disallowedElement) && passesFilters
      if (value instanceof ObjectModelProperty) {
        return filters.every(f => f(value))
      }
      //handle fort built in value types
      else {
        return filters.every(f => f(value))
      }
    })
  }

  get uploaderValueOptions(): IDefaultVOption[] {
    if (!this.origin) return [];
    return this.origin.objectModelPropertiesArray.reduce((acc, omp) => {
      if (omp.is(ObjectModelPropertyRootType.FileAttachment)) {
        acc.push({
          label: omp.label_title || `Untitled ${ObjectModelPropertyName(omp)}`,
          value: omp.client_id,
          labelSuffix: `(${ObjectModelPropertyName(omp)})`
        });
      }
      return acc;
    }, []);
  }

  valueOptions(source: ModelMappingValueOrigin = null): IDefaultVOption[] | Array<IVOptionSetLocal> {
    if (!this.origin) return [];
    const properties = this.origin.objectModelPropertiesArray.filter(p => {
      return p.isNot(ObjectModelPropertyRootType.FileAttachment);
    }).map(omp => {
      return ObjectModelPropertyDefinition.mappingValueOption(omp)
    });
    return [
      {
        heading: "Properties",
        options: properties
      },
      {
        heading: ObjectModelBuiltinCategory.AboutRecord,
        options: this.vOptionsForBuiltinFields(this.aboutRecordProperties)
      },
      {
        heading: ObjectModelBuiltinCategory.ClaimingRecord,
        options: this.vOptionsForBuiltinFields(this.claimingRecordProperties)
      },
    ]
  }

  mapValueToOption(value): IDefaultVOption & { tooltip?: string } {
    let suffix = `(${ObjectModelPropertyName(value) || value.labelSuffix})`
    if (value.isReadOnly) {
      suffix = `${suffix.slice(0, suffix.length - 1)}, ${accessDisplay(value.permissions)})`
    }
    if (value.isIndexed || value.default_property) {
      suffix = `<span class="icon key-empty"></span> ${suffix}`
    }

    let label
    if (_.trim(value.label_title) === '') {
      label = `Untitled ${ObjectModelPropertyName(value)}`
    } else {
      label = value.defaultLabelTitle
    }

    return {
      label: label,
      value: value.client_id,
      labelSuffix: suffix,
      section: value.section ? value.section : null,
      tooltip: value.tooltip,
    }
  }

  /**
   * The reason why there are two methods below instead of one is that there is a model mapping value
   * that is auto-created when a certain rule is chosen (one of the match rules) but is not selectable in the
   * "Add Values" dropdown. So the mapping value needs to look at a different list of options to get its data from
   * compared to the model mapping when it is constructing the options able to be added
   */

  /**
   * @see ObjectModelPropertyDefinition.builtInTypesSet for an explanation of these numbers
   */
  get sourceBuiltInSetNumberForMappingValue(): BuiltInTypesSetNumber {
    if (this.modelMapping.isObjectModelToSf || this.modelMapping.isRelationIdentifierToSf) {
      return 2
    } else if (ModelMappingAllMatchRules.includes(this.modelMapping.rule)) {
      return 3
    } else {
      return 3
    }
  }

  /**
   * Used by to construct the value options as there is a different list in the selection modal sometimes
   */
  get sourceBuiltInSetNumberForValueOptions(): BuiltInTypesSetNumber {
    if (this.modelMapping.isObjectModelToSf || this.modelMapping.isRelationIdentifierToSf) {
      return 2
    } else if (ModelMappingAllMatchRules.includes(this.modelMapping.rule)) {
      // This is the case where the "Record ID" is already added for the user but the user can't choose it as an
      // option themselves from the "Add Values" modal
      return 1
    } else {
      return 1
    }
  }
}